import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import Login from "@/pages/Login";
import POSScreen from "@/pages/POSScreen";
import CalculatorOverlay from "@/components/CalculatorOverlay";
import { MinimizeAppButton } from "@/components/MinimizeAppButton";
import {
  useForcedLogoutInterceptor, useIdleAutoLogout, useAuthHeartbeat,
  useUnlockRegisterOnClose, useLotteryOnlyMode, useBackofficeTerminalConfig,
} from "@/hooks/useAppSessionEffects";

// B3 (V6.13): everything except Login + the POS sale screen is code-split. The register's
// first paint no longer downloads Console / Accounting / Reports / Mobile / marketing code.
const lazyPage = (loader) => lazy(loader);
const ControlCenter = lazyPage(() => import("@/pages/ControlCenter"));
const Products = lazyPage(() => import("@/pages/Products"));
const Reports = lazyPage(() => import("@/pages/Reports"));
const Settings = lazyPage(() => import("@/pages/Settings"));
const QuickSaleCategories = lazyPage(() => import("@/pages/QuickSaleCategories"));
const Promotions = lazyPage(() => import("@/pages/Promotions"));
const CustomerDisplay = lazyPage(() => import("@/pages/CustomerDisplay"));
const Inventory = lazyPage(() => import("@/pages/Inventory"));
const DocumentsPage = lazyPage(() => import("@/pages/DocumentsPage"));
const AccountingPage = lazyPage(() => import("@/pages/AccountingPage"));
const LotteryPage = lazyPage(() => import("@/pages/LotteryPage"));
const LicenseSignup = lazyPage(() => import("@/pages/LicenseSignup"));
const LicenseSignupSuccess = lazyPage(() => import("@/pages/LicenseSignup").then((m) => ({ default: m.LicenseSignupSuccess })));
const LicenseSignupCancel = lazyPage(() => import("@/pages/LicenseSignup").then((m) => ({ default: m.LicenseSignupCancel })));
const BackOfficePortal = lazyPage(() => import("@/pages/OwnerPortal"));
const BackofficeWeb = lazyPage(() => import("@/pages/BackofficeWeb"));
const BackofficeLocal = lazyPage(() => import("@/pages/BackofficeLocal"));
const MarketingHome = lazyPage(() => import("@/pages/MarketingHome"));
const ContactPage = lazyPage(() => import("@/pages/ContactPage"));

// Mobile views
const MobileLayout = lazyPage(() => import("@/pages/mobile/MobileLayout"));
const MobileHome = lazyPage(() => import("@/pages/mobile/MobileHome"));
const MobileLookup = lazyPage(() => import("@/pages/mobile/MobileLookup"));
const MobileProductForm = lazyPage(() => import("@/pages/mobile/MobileProductForm"));
const MobileInventory = lazyPage(() => import("@/pages/mobile/MobileInventory"));
const MobileReports = lazyPage(() => import("@/pages/mobile/MobileReports"));
const MobileReceipts = lazyPage(() => import("@/pages/mobile/MobileReceipts"));
const MobilePromotions = lazyPage(() => import("@/pages/mobile/MobilePromotions"));
const MobilePriceCheck = lazyPage(() => import("@/pages/mobile/MobilePriceCheck"));
const MobileWorksheet = lazyPage(() => import("@/pages/mobile/MobileWorksheet"));
const MobileLottery = lazyPage(() => import("@/pages/mobile/MobileLottery"));

const PageFallback = () => <div className="min-h-screen bg-zinc-50" data-testid="page-loading" />;

import { Toaster } from "@/components/ui/sonner";
import { API } from "@/apiConfig";

// Set to true to enable login screen, false to bypass for development
const ENABLE_AUTH = true;

function App() {
  useForcedLogoutInterceptor();
  useIdleAutoLogout();
  useAuthHeartbeat();

  // Auto-logout on tab/browser close: if sessionStorage marker is missing, clear auth.
  // sessionStorage persists across refresh but clears when tab is closed.
  // Skipped for /customer-display — it's an intentionally public, unauthenticated
  // second-screen route, and opening it in a new tab must NOT log out the POS tab.
  // NOTE: station_id and station_name are NOT cleared here — they identify
  // the physical register and must persist across app restarts.
  if (window.location.pathname !== "/customer-display") {
    if (!sessionStorage.getItem("pos_session")) {
      localStorage.removeItem("auth_session");
      localStorage.removeItem("token");
      localStorage.removeItem("employee");
    }
    sessionStorage.setItem("pos_session", "active");
  }

  // Initialize auth state synchronously from localStorage to prevent redirect on page refresh.
  // The real credential is an httpOnly cookie (set by the backend on login, invisible to JS/XSS) —
  // "auth_session" here is just a non-sensitive UI marker, not a usable credential on its own.
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (!ENABLE_AUTH) return true;
    const hasSession = localStorage.getItem("auth_session");
    const employeeData = localStorage.getItem("employee");
    return !!(hasSession && employeeData);
  });
  const [employee, setEmployee] = useState(() => {
    if (!ENABLE_AUTH) return { id: "dev-employee-001", name: "Demo Cashier", email: "cashier@store.com", role: "cashier" };
    const data = localStorage.getItem("employee");
    return data ? JSON.parse(data) : null;
  });
  const [station, setStation] = useState(() => {
    const stationId = localStorage.getItem("station_id");
    const stationName = localStorage.getItem("station_name");
    return { station_id: stationId || "default", station_name: stationName || "Register 1" };
  });
  // Lottery-Only mode (Distributor Console controlled): POS skips straight to Lottery
  // on login and every other page is blocked. Public endpoint, safe to poll pre-login.
  // Seeded from the last-known cached value so a hard refresh/offline boot never briefly
  // exposes the full app before the fetch resolves (that flash was a real bypass).
  const [lotteryOnly, setLotteryOnly] = useState(() => localStorage.getItem("lottery_only_cache") === "1");
  const [lotteryOnlyReady, setLotteryOnlyReady] = useState(() => localStorage.getItem("lottery_only_cache") !== null);
  useLotteryOnlyMode(setLotteryOnly, setLotteryOnlyReady);

  // Console "Back Office" toggle for THIS terminal (Electron config, set from the license
  // verify at app start): the terminal lands on Control Center instead of the sale screen.
  const [backofficeTerminal, setBackofficeTerminal] = useState(() => localStorage.getItem("backoffice_terminal") === "1");
  useBackofficeTerminalConfig(setBackofficeTerminal);

  // Every /pos/* page except /pos/lottery itself goes through this — blocks rendering
  // until lotteryOnly is known (no cache-miss flash), then either redirects to Lottery
  // or renders the real page.
  const posRoute = (element, { saleScreen = false } = {}) => {
    if (!isAuthenticated) return ENABLE_AUTH ? <Navigate to="/pos/login" replace /> : <div>Loading...</div>;
    if (!lotteryOnlyReady) return <div className="min-h-screen bg-zinc-50" />;
    if (lotteryOnly) return <Navigate to="/pos/lottery" replace />;
    if (backofficeTerminal && saleScreen) return <Navigate to="/pos/control-center" replace />;
    return element;
  };
  const homeRoute = lotteryOnly ? "/pos/lottery" : backofficeTerminal ? "/pos/control-center" : "/pos";

  useEffect(() => {
    if (!ENABLE_AUTH) {
      // Development mode: set dummy token if not already set
      if (!localStorage.getItem("token")) {
        const defaultEmployee = {
          id: "dev-employee-001",
          name: "Demo Cashier",
          email: "cashier@store.com",
          role: "cashier"
        };
        localStorage.setItem("token", "dev-bypass-token");
        localStorage.setItem("employee", JSON.stringify(defaultEmployee));
        setIsAuthenticated(true);
        setEmployee(defaultEmployee);
      }
    }
  }, []);

  const handleLogin = (token, employeeData, boundStation) => {
    // The backend already set an httpOnly "access_token" cookie on this same-origin login
    // response — that cookie (invisible to JS) is now the real credential for all API calls.
    // Only keep the raw token in localStorage for the Electron "remote_api_url" debug override,
    // where the app talks cross-origin to a remote server and cookies can't follow automatically.
    if (localStorage.getItem("remote_api_url")) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
    localStorage.setItem("auth_session", "1");
    localStorage.setItem("employee", JSON.stringify(employeeData));
    setIsAuthenticated(true);
    setEmployee(employeeData);
    
    // Auto-set store timezone from client browser
    fetch(`${API}/settings/timezone`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ tz_offset: new Date().getTimezoneOffset() })
    }).catch(() => {});

    if (boundStation && boundStation.station_id) {
      // This PC's terminal is already bound centrally (Electron Setup / Change Terminal) —
      // log straight in as that terminal. Each PC has its own dedicated terminal, so there
      // is no register picker to show.
      localStorage.setItem("station_id", boundStation.station_id);
      localStorage.setItem("station_name", boundStation.station_name);
      setStation(boundStation);
    } else {
      // Browser/dev fallback — no Electron terminal binding, use a single default register.
      const fallback = { station_id: "default", station_name: "Register 1" };
      localStorage.setItem("station_id", fallback.station_id);
      localStorage.setItem("station_name", fallback.station_name);
      setStation(fallback);
    }
  };

  const handleLogout = () => {
    // Unlock the register before logging out
    const stationId = localStorage.getItem("station_id");
    const fallbackToken = localStorage.getItem("token");
    if (stationId) {
      const url = `${API}/registers/unlock?station_id=${stationId}`;
      fetch(url, { method: "POST", headers: fallbackToken ? { Authorization: `Bearer ${fallbackToken}` } : {}, keepalive: true }).catch(() => {});
    }
    // Clear the httpOnly auth cookie server-side (JS can't delete an httpOnly cookie directly)
    fetch(`${API}/employees/logout`, { method: "POST" }).catch(() => {});

    if (!ENABLE_AUTH) {
      window.location.reload();
    } else {
      localStorage.removeItem("auth_session");
      localStorage.removeItem("token");
      localStorage.removeItem("employee");
      localStorage.removeItem("station_id");
      localStorage.removeItem("station_name");
      sessionStorage.removeItem("pos_session");
      setIsAuthenticated(false);
      setEmployee(null);
      setStation(null);
    }
  };

  // Unlock register when browser tab/window closes
  useUnlockRegisterOnClose();

  return (
    <div className="App">
      <MinimizeAppButton show={lotteryOnly || backofficeTerminal} />
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public marketing site */}
          <Route path="/" element={<MarketingHome />} />
          <Route path="/contact" element={<ContactPage />} />

          {ENABLE_AUTH && (
            <Route
              path="/pos/login"
              element={
                !isAuthenticated ? (
                  <Login onLogin={handleLogin} />
                ) : !lotteryOnlyReady ? (
                  <div className="min-h-screen bg-zinc-50" />
                ) : (
                  <Navigate to={homeRoute} replace />
                )
              }
            />
          )}
          <Route path="/pos" element={posRoute(<POSScreen employee={employee} onLogout={handleLogout} station={station} />, { saleScreen: true })} />
          <Route path="/pos/control-center" element={posRoute(<ControlCenter employee={employee} onLogout={handleLogout} station={station} backofficeTerminal={backofficeTerminal} />)} />
          <Route path="/pos/products" element={posRoute(<Products employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/reports" element={posRoute(<Reports employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/settings" element={posRoute(<Settings employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/quick-sale" element={posRoute(<QuickSaleCategories employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/promotions" element={posRoute(<Promotions employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/inventory" element={posRoute(<Inventory employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/invoices" element={posRoute(<DocumentsPage employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route path="/pos/accounting" element={posRoute(<AccountingPage employee={employee} onLogout={handleLogout} station={station} />)} />
          <Route
            path="/pos/lottery"
            element={
              isAuthenticated ? (
                <LotteryPage employee={employee} onLogout={handleLogout} station={station} />
              ) : (
                ENABLE_AUTH ? <Navigate to="/pos/login" replace /> : <div>Loading...</div>
              )
            }
          />
          {/* Customer Display - No auth required, runs on secondary monitor */}
          <Route
            path="/customer-display"
            element={<CustomerDisplay />}
          />
          {/* License signup — used only by the central deployed instance to onboard new stores. Public, no auth. */}
          <Route path="/license-signup" element={<LicenseSignup />} />
          <Route path="/license-signup/success" element={<LicenseSignupSuccess />} />
          <Route path="/license-signup/cancel" element={<LicenseSignupCancel />} />
          {/* Distributor & License Console — multi-user, role-based (Owner/User), TOTP-protected. */}
          <Route path="/console" element={<BackOfficePortal />} />
          <Route path="/backoffice" element={<BackofficeWeb />} />
          <Route path="/backoffice-local" element={<BackofficeLocal />} />
          <Route path="/owner-portal" element={<Navigate to="/console" replace />} />
          <Route path="/central-app" element={<Navigate to="/mobile" replace />} />
          {/* Mobile POS - Store floor interface */}
          <Route path="/mobile" element={<MobileLayout />}>
            <Route index element={<MobileHome />} />
            <Route path="lookup" element={<MobileLookup />} />
            <Route path="products/add" element={<MobileProductForm />} />
            <Route path="products/edit/:id" element={<MobileProductForm />} />
            <Route path="inventory" element={<MobileInventory />} />
            <Route path="reports" element={<MobileReports />} />
            <Route path="receipts" element={<MobileReceipts />} />
            <Route path="price-check" element={<MobilePriceCheck />} />
            <Route path="promotions" element={<MobilePromotions />} />
            <Route path="worksheet" element={<MobileWorksheet />} />
            <Route path="lottery" element={<MobileLottery />} />
          </Route>
          <Route path="*" element={<Navigate to="/pos" replace />} />
        </Routes>
        </Suspense>
        <CalculatorOverlay />
      </BrowserRouter>
      <Toaster position="top-right" duration={2000} />
    </div>
  );
}

export default App;
